import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema, convertImageFromDto } from "@modules/dtos/imageDto"
import { PaymentError, PaymentErrorDto } from "@modules/finances/types/stripeErrorTypes"
import { UserDtoSchema, convertUserFromDto } from "@modules/profile/profileDtos"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffStatus, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import {
  ItemProgram,
  LightProgram,
  LightProgramInfo,
  Program,
  ProgramPayment,
  ProgramPrice,
  ProgramType,
  ProgramVisibility,
  SharedCoachStatus,
  UserProgramItem,
} from "@modules/programs/programTypes"
import {
  ItemProgramInfiniteDtoSchema,
  ProgramInfiniteDto,
  convertItemProgramInfiniteFromDto,
} from "@modules/programs/services/programInfiniteDto"
import {
  ItemProgramOnOffDto,
  ItemProgramOnOffDtoSchema,
  ProgramOnOffDto,
  convertItemProgramOnOffFromDto,
} from "@modules/programs/services/programOnOffDto"
import { PromoCodeDto, convertPromoCodeFromDto } from "@modules/promoCode/services/promoCodeDto"
import { z } from "zod"

// Refactor this schema because it's not related to backend's schema
export const ItemProgramDtoSchema = z.union([ItemProgramInfiniteDtoSchema, ItemProgramOnOffDtoSchema])

// Used in program bundles API & user-programs API
export const UserProgramItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  image: HUImageDtoSchema.nullable(),
  owner: UserDtoSchema,
  visibility: z.nativeEnum(ProgramVisibility),
  subscribed: z.boolean(),
  subscriptionCount: z.number(),
  price: PriceObjectDtoSchema.nullable(),
  defaultHideSessionContent: z.boolean(),
  status: z.nativeEnum(ProgramOnOffStatus),
  duration: z.number().nullable(),
  type: z.string(),
  sharedCoachStatus: z.nativeEnum(SharedCoachStatus).nullable(),
  followedInBundle: z.boolean(),
})

// used in promocode API
export const LightProgramInfoDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  price: PriceObjectDtoSchema.nullable(),
  type: z.string(),
})

// used in b2b-programs API
export const WebLightProgramDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  descripition: z.string(),
  image: HUImageDtoSchema,
  owner: UserDtoSchema,
  visibility: z.nativeEnum(ProgramVisibility),
  scoreboardVisibility: z.boolean(),
  type: z.nativeEnum(ProgramType),
})

export type ItemProgramDto = z.infer<typeof ItemProgramDtoSchema>
export type ProgramDto = ProgramInfiniteDto | ProgramOnOffDto
export type LightProgramInfoDto = z.infer<typeof LightProgramInfoDtoSchema>
export type WebLightProgramDto = z.infer<typeof WebLightProgramDtoSchema>
export type UserProgramItemDto = z.infer<typeof UserProgramItemDtoSchema>

export function convertItemProgramFromDto(dto: ItemProgramDto): ItemProgram {
  if (dto.type === ProgramType.VIRTUAL) {
    return convertItemProgramInfiniteFromDto(dto)
  } else return convertItemProgramOnOffFromDto(dto as ItemProgramOnOffDto)
}

export function convertProgramFromDto(dto: ProgramDto): Program {
  return {
    ...convertItemProgramFromDto(dto),
    description: dto.description ?? dto.description,
  }
}

export type ProgramPriceDto = {
  currency: string
  value: number
}

export type ProgramPaymentDto = {
  amount: ProgramPriceDto
  promoCode: PromoCodeDto
  error?: PaymentErrorDto
}

export function convertProgramPriceFromDto(
  dto: ProgramPriceDto | null | undefined,
  allowZero?: boolean,
): ProgramPrice | null {
  return dto?.currency && ((allowZero && dto.value >= 0) || dto.value)
    ? {
        currency: dto.currency,
        value: dto.value,
      }
    : null
}

export function convertProgramPaymentFromDto(dto: ProgramPaymentDto | undefined): ProgramPayment | undefined {
  return dto?.amount
    ? {
        amount: {
          currency: dto.amount.currency,
          value: dto.amount.value,
        },
        promoCode: dto.promoCode ? convertPromoCodeFromDto(dto.promoCode) : undefined,
        error: dto.error as PaymentError,
      }
    : undefined
}

export function convertLightProgramInfoFromDto(dto: LightProgramInfoDto): LightProgramInfo {
  return {
    ...dto,
    name: dto.name,
    image: dto.image ? convertImageFromDto(dto.image) : null,
    price: convertProgramPriceFromDto(dto.price),
    _programType: dto.type === ProgramType.VIRTUAL ? ProgramInfiniteTypeOf : ProgramOnOffTypeOf,
  }
}

export function convertUserProgramItemFromDto(dto: UserProgramItemDto): UserProgramItem {
  return {
    ...dto,
    title: dto.name,
    _programType: dto.type === ProgramType.VIRTUAL ? ProgramInfiniteTypeOf : ProgramOnOffTypeOf,
    image: dto.image ? convertImageFromDto(dto.image) : null,
    owner: convertUserFromDto(dto.owner),
    price: convertProgramPriceFromDto(dto.price),
    sharedCoachStatus: dto.sharedCoachStatus ?? null,
  }
}

export function convertLightProgramFromDto(dto: WebLightProgramDto): LightProgram {
  return {
    ...dto,
    image: convertImageFromDto(dto.image),
    owner: convertUserFromDto(dto.owner),
  }
}
